import { ChartItem, GraphOptions, Loan } from "@/types";
import { first, initial, last, tail } from "lodash-es";
import { Ref } from "vue";
import * as d3 from "d3";
import { formatNumberToString, getCurrency } from "./number-format";

export function useCostChart(chartRef: Ref<SVGGElement | undefined>, options: GraphOptions = {
  width: 1000,
  height: 300,
  margin: 60,
}) {

  function renderChart(costs: number[], loan: Loan | undefined, breakEvenPointYear?: number) {
    if (!chartRef.value) return;

    chartRef.value.innerHTML = "";

    const { width, height, margin } = options;

    const items = tail(
      costs.map((num, index, arr) =>
        toItem(arr, num, index, loan)
      )
    );

    // extended items used for scales
    const extendedItems = tail(
      extendItems(costs).map((num, index, arr) =>
        toItem(arr, num, index, loan)
      )
    );

    const yearsBand = d3
      .scaleBand()
      .range([0, options.width])
      .domain(extendedItems.map((s) => s.i) as any[])
      .padding(0.2);

    const yearsScale = d3
      .scaleLinear()
      .range([0, options.width])
      .domain(
        d3.extent(extendedItems.map((s) => s.i)) as [number, number]
      );

    const moneyScale = d3
      .scaleLinear()
      .range([options.height, 0, 0.01])
      .domain(
        d3
          .extent(extendedItems.map((s) => s.value).concat([2000]))
          .map((e: number | undefined = 0, i: number) =>
            i ? e + 2e3 : e - 2e3
          )
      );


    const svg = d3
      .select(chartRef.value)
      .attr("viewBox", `0 0 ${width + margin} ${height + 2 * margin}`);

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin}, ${margin})`);


    /***** Drawings *****/
    investmentPayOffFill(chart, items, yearsScale, moneyScale);
    drawAxes(chart, yearsBand, moneyScale, options);
    drawBars(chart, items || [], "repayment", yearsBand, moneyScale);
    drawBars(chart, items || [], "benefit", yearsBand, moneyScale);
    investmentPayOffLine(chart, extendedItems, yearsScale, moneyScale);

    if (breakEvenPointYear) {
      drawBreakEven(
        chart,
        breakEvenPointYear,
        items || [],
        yearsScale,
        moneyScale
      );
    }
  }

  return { renderChart }
}

/**
 * Draw axes 
 * @param chart 
 * @param yearsBand 
 * @param moneyScale 
 * @param options 
 */
function drawAxes(
  chart: d3.Selection<SVGGElement, unknown, null, undefined>,
  yearsBand: d3.ScaleBand<string>,
  moneyScale: d3.ScaleLinear<number, number, never>,
  options: GraphOptions
) {
  const { width, height } = options;
  const format = {
    style: "currency",
    currency: getCurrency(),
    minimumFractionDigits: 0,
  };

  // X axis
  chart
    .append("g")
    .attr("transform", `translate(0, ${height})`)
    // .attr("stroke", "pink")
    // .attr("class", "grid")
    .call(
      d3
        .axisBottom(yearsBand)
        .tickSize(-height)
        .tickFormat((d) => d)
    );

  // Y axis
  chart
    .append("g")
    .attr("class", "grid")
    .call(
      d3
        .axisLeft(moneyScale)
        .ticks(9)
        .tickSize(-width)
        .tickFormat((d: d3.AxisDomain): string =>
          formatNumberToString(d as number, format)
        )
    );

  // Years label
  chart
    .selectAll()
    .data(["Years"])
    .enter()
    .append("text")
    .text((d) => d as string)
    .attr("x", width - 50)
    .attr("y", height - 5)
    .attr("font-size", 12)
    .attr("class", "tick")
    .attr("class", "axisLabel");

  // Profits Label
  chart
    .append("g")
    .attr("transform", "rotate(90)")
    .attr("class", "yAxeLabel")
    .selectAll(".yAxeLabel")
    .data(["Profits"])
    .enter()
    .append("text")
    .text((d) => d as string)
    .attr("x", 10)
    .attr("y", -5)
    .attr("font-size", 10)
    .attr("class", "axisLabel");
}


function investmentPayOffLine(
  chart: d3.Selection<SVGGElement, unknown, null, undefined>,
  extendedItems: ChartItem[],
  yearsScale: d3.ScaleLinear<number, number, never>,
  moneyScale: d3.ScaleLinear<number, number, never>
) {

  // dotted line
  chart
    .append("path")
    .attr("class", "investment-pay-off-dots")
    .attr(
      "d",
      drawPath(extendedItems, "value", false, yearsScale, moneyScale)
    );
}

function investmentPayOffFill(
  chart: d3.Selection<SVGGElement, unknown, null, undefined>,
  items: ChartItem[],
  yearsScale: d3.ScaleLinear<number, number, never>,
  moneyScale: d3.ScaleLinear<number, number, never>
) {
  // line fill
  chart
    .append("path")
    .attr("class", "investment-pay-off-fill")
    .attr(
      "d",
      drawPath(items || [], "value", true, yearsScale, moneyScale)
    );
}

function drawBars(
  chart: d3.Selection<SVGGElement, unknown, null, undefined>,
  theItems: ChartItem[],
  property: keyof ChartItem,
  yearsBand: d3.ScaleBand<string>,
  moneyScale: d3.ScaleLinear<number, number, never>
) {
  chart
    .selectAll()
    .data(theItems)
    .enter()
    .append("rect")
    .attr("x", (s: ChartItem) => yearsBand(s.i as any) as number)
    .attr("y", (s: ChartItem) => moneyScale(Math.max(s[property], 0)))
    .attr("height", (s: ChartItem) =>
      Math.abs(moneyScale(s[property]) - moneyScale(0))
    )
    .attr("width", yearsBand.bandwidth())
    .attr("class", `${property}-bar`);
}

function drawBreakEven(
  chart: d3.Selection<SVGGElement, unknown, null, undefined>,
  breakEvenPointYear: number,
  chartItems: ChartItem[],
  yearsScale: d3.ScaleLinear<number, number, never>,
  moneyScale: d3.ScaleLinear<number, number, never>
) {
  // break even line
  chart
    .append("path")
    .attr(
      "d",
      drawBreakEvenLine(
        breakEvenPointYear,
        chartItems,
        yearsScale,
        moneyScale
      )
    )
    .attr("class", "break-even-line");

  // circle
  chart
    .append("circle")
    .attr("r", 7)
    .attr("cx", yearsScale(breakEvenPointYear - 1))
    .attr("cy", moneyScale(0))
    .attr("class", "break-even-point");

  // break even text
  chart
    .selectAll()
    .data(["Break even point"])
    .enter()
    .append("text")
    .text((d) => d as string)
    .attr("x", yearsScale(breakEvenPointYear - 1))
    .attr("y", moneyScale(0))
    .attr("class", "break-even-label");
}

function drawBreakEvenLine(
  breakEvenPointYear: number,
  chartItems: ChartItem[],
  yearsScale: d3.ScaleLinear<number, number, never>,
  moneyScale: d3.ScaleLinear<number, number, never>
): string {
  const firstItem = first(chartItems)?.value || 0;
  const lastItem = last(chartItems)?.value || 0;

  return drawPolyline([
    [yearsScale(breakEvenPointYear - 1), moneyScale(lastItem)],
    [
      yearsScale(breakEvenPointYear - 1),
      moneyScale(-Math.abs(firstItem || 0) / 2),
    ],
  ]);
}

function drawPath(
  items: ChartItem[],
  property: keyof ChartItem,
  isFill = true,
  yearsScale: d3.ScaleLinear<number, number, never>,
  moneyScale: d3.ScaleLinear<number, number, never>
) {
  return drawPolyline(
    items
      .map((d: ChartItem): [number, number] => [
        yearsScale(d.i),
        moneyScale(d[property]),
      ])
      .concat(
        isFill
          ? [
            [yearsScale((last(items) as ChartItem).i), moneyScale(0)],
            [yearsScale(first(items)?.i || 0), moneyScale(0)],
          ]
          : []
      )
  );
}

function drawPolyline(coords: [number, number][]) {
  let d = `M${coords[0].join(",")}`;
  tail(coords).forEach((c) => {
    d += `L${c.join(",")}`;
  });
  return d;
}


/**
 * Converts to chart item
 * @param chartData 
 * @param item 
 * @param index 
 * @param selectedLoan 
 * @returns 
 */
function toItem(
  chartData: number[],
  item: number,
  index: number,
  selectedLoan: Loan | undefined
): ChartItem {
  return {
    value: item,
    repayment: selectedLoan
      ? index > selectedLoan.term
        ? 0
        : selectedLoan.term
          ? -(selectedLoan.amount / selectedLoan.term)
          : 0
      : 0,
    i: index,
    benefit: index ? item - chartData[index - 1] : 0,
  };
}

/**
 * Extends graph items to be used as grid number
 * @param items 
 * @returns 
 */
function extendItems(items: number[]) {
  if (items.length < 2) {
    return items;
  }
  return items.concat([
    ((last(items) as number) - (last(initial(items)) as number)) * 1.07 +
    (last(items) as number),
  ]);
}